import { INTERCOM_ID } from '~/constants';
import { setTheme, toggleInformation } from '~/logic';
import { SubscriptionPlan } from '@cirqll/sdk/enum';
import { i18n } from '~/modules/i18n';
import { Intercom } from '@intercom/messenger-js-sdk';
import * as sdk from '@cirqll/sdk';

/**
 * Downloads a media file through its signed download url, which core always serves as an attachment.
 */
export function downloadMedia(media: sdk.Media) {
    window.location.assign(media.download_url);
}

/**
 * Counts items which are in the past
 *
 * @export
 * @param {(key: string, plural?: number) => string} t
 * @param {Customer} customer
 * @param {sdk.Subscription} subscription
 * @param {boolean} [pretty=false]
 * @return {string} customer status
 */
export function expiredCustomerAttention(t: (key: string, plural?: number) => string, customer: { stats: { appointmentsPast: number; contractsPast: number; tasksPast: number } }, subscription: sdk.Subscription, pretty = false) {
    const stats = customer.stats;
    let warnings = [];
    if (stats.appointmentsPast > 0) {
        warnings.push(t('attention.expired_appointment', stats.appointmentsPast));
    }
    if (stats.contractsPast > 0 && subscription.plan !== SubscriptionPlan.BASIC) {
        warnings.push(t('attention.expired_contracts', stats.contractsPast));
    }
    if (stats.tasksPast > 0) {
        warnings.push(t('attention.expired_tasks', stats.tasksPast));
    }
    if (warnings.isEmpty()) {
        return t('attention.something_expired');
    } else if (pretty) {
        warnings = warnings.map((warning) => `<strong>${warning}</strong>`);
        const last = warnings.pop();
        if (warnings.isNotEmpty()) {
            return `${warnings.join(', ')} ${t('basic.and')} ${last}`;
        } else {
            return last;
        }
    } else {
        return warnings.join('\n');
    }
}

/**
 * Returns subtasks string like: (1/2)
 * Meaning 1 out of 2 completed
 *
 * @export
 * @param {sdk.Task[]} subtasks
 * @return {string} count
 */
export function subtasks(subtasks: sdk.Subtask[]): string {
    const total = subtasks.length;
    if (total === 0) {
        return '';
    } else {
        const finished = subtasks.reduce((done, subtask) => done + (subtask.finished_at ? 1 : 0), 0);
        return `(${finished}/${total})`;
    }
}

/**
 * Returns subtasks string like: (1/2)
 * Meaning 1 out of 2 completed
 *
 * @export
 * @param {sdk.Task} task
 * @return {string} count
 */
export function subtasks2(task: sdk.Task): string {
    if (!('subtasks_count' in task)) {
        throw new Error('missing subtasks_count field');
    }
    if (!('subtasks' in task)) {
        throw new Error('missing subtasks relation');
    }

    const total = task.subtasks?.total;
    if (!total) {
        return '';
    } else {
        return `(${task.subtasks_count}/${total})`;
    }
}

/**
 * Preload data before app startup
 * Data is loaded in their respective store
 * If it was already loaded, wont't load again (#fetchOnce())
 *
 * @export
 * @return {void} hasFetchedData
 */
export function preload(user: sdk.UserMe): void {
    toggleInformation(user.settings.information);
    setTheme(user.settings.color_mode);
    i18n.global.locale.value = user.language;

    void sdk.queryClient.query(sdk.field.index({ limit: 1000 }));

    if (INTERCOM_ID) {
        Intercom({
            app_id: INTERCOM_ID,
            region: 'eu',
            name: user.name,
            email: user.email,
            user_id: user.id.toString(),
            user_hash: user.intercom_hash,
            created_at: Math.floor(user.created_at.getTime() / 1000),
        });
    }
}

/**
 * @param str to split
 * @param delim to split
 * @param count max items
 * @returns split parts
 */
export function splitWithTail(str: undefined | string, delim: string, count: number): string[] {
    if (!str) return [];
    count -= 1;
    const parts = str.split(delim);

    return [...parts.splice(0, count), parts.join(delim)];
}
